import Image from 'next/image';
import { useEffect, useMemo, useRef } from 'react';
import useObjectVisibility from '@hooks/useObjectVisibility';
import {
  HeroTrackPromotionEventProps,
  InteractionLocation,
  PromotionEventName,
  trackPromotionEvent,
} from '@utils/gtm/ga4/trackPromotionEvents';
import { determinePageType, buildCreativeSlotName } from '@utils/gtm/ga4/utils';

import {
  ImageVariations,
  HeroSlice,
} from '@adapters/prismic/types/brandedStorePage';
import YouTubeEmbed from '@components/cms/Slices/MediaEmbed/YouTubeEmbed';
import HeroTextBlock from './HeroTextBlock';
import Container from '@components/global/Container';

import styles from './Hero.module.scss';

export const Hero = ({
  primary,
  locale = 'en',
  sliceIndex,
  brandedStoreCoopAttribute,
}: HeroSlice) => {
  const heroRef = useRef<HTMLDivElement>(null);
  const isHeroVisible = useObjectVisibility(heroRef);

  const videoAspectRatio = useMemo(() => {
    if (Number(primary?.video_width) && Number(primary?.video_height)) {
      return (
        (Number(primary?.video_height) / Number(primary?.video_width)) * 100
      );
    }
    return 42.19;
  }, [primary?.video_width, primary?.video_height]);

  const promotionDetails = useMemo(
    () => ({
      page: {
        path: typeof window === 'undefined' ? '' : window.location.pathname,
        page_type: determinePageType(),
      },
      interaction: {
        coop_brand: brandedStoreCoopAttribute ?? null,
        interaction_location: 'hero',
      },
      ecommerce: {
        creative_name: `image,${primary?.image?.url}`,
        creative_slot: buildCreativeSlotName({
          sliceIndex,
          interactionLocation: InteractionLocation.hero,
        }),
        promotion_id: primary?.title ?? null,
        promotion_name: primary?.cta_label ?? null,
        isVideo: false,
      },
    }),
    [brandedStoreCoopAttribute, primary, sliceIndex]
  );

  useEffect(() => {
    if (isHeroVisible) {
      trackPromotionEvent({
        eventName: PromotionEventName.viewPromotion,
        ...promotionDetails,
      } as HeroTrackPromotionEventProps);
    }
  }, [
    brandedStoreCoopAttribute,
    isHeroVisible,
    primary,
    promotionDetails,
    sliceIndex,
  ]);

  const images = primary?.image && (
    <>
      {primary?.image && (
        <div className={styles.desktopImage}>
          <Image
            src={primary.image?.url}
            alt={primary.image?.alt || ''}
            width={primary?.image?.dimensions?.width}
            height={primary?.image?.dimensions?.height}
            priority={sliceIndex === 0 ? true : false}
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        </div>
      )}
      {(primary?.image_mobile?.url || primary?.mobile_image?.url) && (
        <div className={styles.mobileImage}>
          <Image
            src={primary.image_mobile?.url || primary?.mobile_image?.url || ''}
            alt={primary.image_mobile?.alt || primary?.mobile_image?.alt || ''}
            width={
              primary?.image_mobile?.dimensions?.width ||
              primary?.mobile_image?.dimensions?.width
            }
            height={
              primary?.image_mobile?.dimensions?.height ||
              primary?.mobile_image?.dimensions?.height
            }
            priority={sliceIndex === 0 ? true : false}
            sizes="100vw"
            style={{
              width: '100%',
              height: 'auto',
            }}
          />
        </div>
      )}
    </>
  );

  const handleCTAClick = () => {
    trackPromotionEvent({
      eventName: PromotionEventName.selectPromotion,
      ...promotionDetails,
    } as HeroTrackPromotionEventProps);
  };

  return (
    <Container>
      <div id={primary?.id} className={styles.heroContainer} ref={heroRef}>
        {!primary?.text_below_image &&
          (primary?.logo?.url ||
            primary?.title ||
            primary?.copy?.[0]?.text ||
            primary?.cta_label) && (
            <HeroTextBlock
              primary={primary}
              locale={locale}
              handleClick={handleCTAClick}
            />
          )}
        <div className={styles.heroImageContainer}>
          {primary?.image &&
          primary?.image_variations &&
          primary?.image_variations === ImageVariations.image ? (
            images
          ) : (
            <div className={styles.videoWrapper}>
              <YouTubeEmbed
                html={`<iframe width="100%" height="100%" src="https://www.youtube.com/embed/${primary?.video_id}?feature=oembed&enablejsapi=1&controls=0&autoplay=1&mute=1&loop=1&playsinline=1&modestbranding=1&rel=0&autohide=1&playlist=${primary?.video_id}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`}
                aspectRatio={videoAspectRatio}
                videoWrapperClassName
              />
            </div>
          )}
        </div>
        {primary?.text_below_image &&
          (primary?.logo?.url ||
            primary?.title ||
            primary?.copy?.[0]?.text ||
            primary?.cta_label) && (
            <HeroTextBlock
              primary={primary}
              locale={locale}
              handleClick={handleCTAClick}
            />
          )}
      </div>
    </Container>
  );
};

export default Hero;
